// src/pages/healthcare/patient/modalComponents/submodals/AssignPractitionerToSampleModal.tsx

import React, { useState, useEffect } from "react";
import { Modal, Button, Typography, Space } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import { useNotifications } from "../../../../../context/NotificationContext";

import { fetchTeamMembers } from "../../../../../api/admin/teamApi";
import { createAuthSamples, activatePatientAuthSamples, fetchPatientAuthSamples } from "../../../../../api/geneCodes/AuthSamples";
import { fetchPanels, createOrUpdateSamplesPanels } from "../../../../../api/geneCodes/PanelApi";

import { TeamMember } from "../../../../../types/admin/TeamTypes";
import { Patient } from "../../../../../types/healthcare/PatientTypes";
import { Panel } from "../../../../../types/geneCodes/Panel";
import { Sample } from "../../../../../types/lab/SampleTypes";
import { DataTable, StatusTag } from "../../../../../components/shared";
import type { UserUuid } from "../../../../../types/identity";

const { Text } = Typography;

interface AssignPractitionerToSampleModalProps {
	visible: boolean;
	onClose: () => void;
	patient: Patient;
	sampleId: string;
}

const AssignPractitionerToSampleModal: React.FC<AssignPractitionerToSampleModalProps> = ({ visible, onClose, patient, sampleId }) => {
	const [practitioners, setPractitioners] = useState<TeamMember[]>([]);
	const [allPanels, setAllPanels] = useState<Panel[]>([]);
	const [loading, setLoading] = useState(false);
	const [assigning, setAssigning] = useState<UserUuid | null>(null);

	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		if (visible) {
			loadData();
		}
	}, [visible, patient.id]);

	const loadData = async () => {
		setLoading(true);
		try {
			const [{data, total}, panelsResponse] = await Promise.all([fetchTeamMembers(1, 100), fetchPanels()]);

			setPractitioners(data);
			setAllPanels(panelsResponse.data);
		} catch (error) {
			console.error("Error loading data:", error);
			showError("Failed to load practitioners");
		} finally {
			setLoading(false);
		}
	};

	const handleAssignPractitioner = async (practitioner: TeamMember) => {
		if (!practitioner.email) {
			showError("Practitioner email not found");
			return;
		}

		setAssigning(practitioner.id);
		try {
			// 1. Create auth sample for practitioner
			await createAuthSamples(practitioner.email, sampleId);

			// 2. Get the auth sample ID
			const authSamples = await fetchPatientAuthSamples(practitioner.email);
			const matchingSample = authSamples.find((sample: Sample) => sample.sampleID === sampleId);
			

			if (!matchingSample) {
				throw new Error("Failed to create auth sample");
			}

			const authSampleId = matchingSample.idauth_samples;

			// 3. Activate the sample with no deactivation date
			await activatePatientAuthSamples(authSampleId as number);

			// 4. Assign all panels to the practitioner
			const allPanelIds = allPanels.map((panel) => panel.id_panels);
			await createOrUpdateSamplesPanels(authSampleId, allPanelIds);

			showSuccess(`Successfully assigned sample access to ${practitioner.first_name} ${practitioner.last_name}`);
		} catch (error: any) {
			console.error("Error assigning practitioner:", error);
			showError(error.response?.data?.message || "Failed to assign sample to practitioner");
		} finally {
			setAssigning(null);
		}
	};

	const columns = [
		{
			title: "Name",
			key: "name",
			render: (_: any, record: TeamMember) => <Text>{`${record.first_name} ${record.last_name}`}</Text>,
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
		},
		{
			title: "Role",
			dataIndex: "user_type",
			key: "user_type",
			width: 150,
			render: (userType: string) => <StatusTag status={userType?.toLowerCase()} label={userType?.replace("_", " ").toUpperCase()} />,
		},
		{
			title: "Action",
			key: "action",
			width: 150,
			render: (_: any, record: TeamMember) => (
				<Button type="primary" size="small" icon={<UserAddOutlined />} onClick={() => handleAssignPractitioner(record)} loading={assigning === record.id} disabled={assigning !== null && assigning !== record.id}>
					Assign Access
				</Button>
			),
		},
	];

	return (
		<Modal
			title={
				<Space>
					<UserAddOutlined />
					<span>Assign Practitioners to Sample</span>
				</Space>
			}
			open={visible}
			onCancel={onClose}
			width={800}
			destroyOnClose
			footer={[
				<Button key="close" onClick={onClose}>
					Close
				</Button>,
			]}
		>
			<div style={{ marginBottom: 16 }}>
				<Space direction="vertical" size={4}>
					<Text strong>Patient: {`${patient.first_name} ${patient.last_name}`}</Text>
					<Text type="secondary">Sample ID: {sampleId}</Text>
					<Text type="secondary" style={{ fontSize: 12 }}>
						This will grant the practitioner full access to this sample with all categories activated
					</Text>
				</Space>
			</div>

			<DataTable
				columns={columns}
				dataSource={practitioners}
				rowKey="id"
				loading={loading}
				pagination={false}
				emptyTitle="No practitioners assigned to this patient"
			/>
		</Modal>
	);
};

export default AssignPractitionerToSampleModal;
